import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Pagination, Alert, Badge, Tabs, Tab } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Event, ActivityPlace } from '../types';
import EventCard from '../components/EventCard';
import ActivityCard from '../components/ActivityCard';
import SkeletonLoader, { EventCardSkeleton, ActivityCardSkeleton } from '../components/common/SkeletonLoader';
import SEO from '../components/common/SEO';
import './SearchPage.scss';

const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryParam = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'all' | 'events' | 'activities'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<{
    query: string;
    events: { results: Event[]; pagination: any };
    activities: { results: ActivityPlace[]; pagination: any };
  } | null>(null);

  const fetchSearchResults = async (page: number = 1, forceRefresh: boolean = false) => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await apiService.search(
        searchQuery.trim(),
        categoryFilter || undefined,
        eventTypeFilter || undefined,
        page,
        forceRefresh
      );
      
      setSearchResults(data);
      setCurrentPage(page);
    } catch (err) {
      setError('Failed to load search results');
      console.error('Error fetching search results:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (queryParam) {
      setSearchQuery(queryParam);
      fetchSearchResults(1, true);
    }
  }, [queryParam]);

  useEffect(() => {
    const handleCountryChange = () => {
      apiService.cache.clearByPattern('^search');
      // Add a small delay to ensure session is saved on backend
      setTimeout(() => {
        if (searchQuery.trim()) {
          fetchSearchResults(1, true);
        }
      }, 200);
    };

    window.addEventListener('countryChanged', handleCountryChange as EventListener);
    return () => {
      window.removeEventListener('countryChanged', handleCountryChange as EventListener);
    };
  }, [searchQuery, categoryFilter, eventTypeFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      apiService.cache.clearByPattern('^search');
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      fetchSearchResults(1, true);
    }
  };

  const handlePageChange = (page: number, type: 'events' | 'activities') => {
    if (type === 'events') {
      // For now, we'll use the same page for both
      // In a more advanced implementation, you could track separate pages
      fetchSearchResults(page);
    } else {
      fetchSearchResults(page);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = (pagination: any, type: 'events' | 'activities') => {
    if (!pagination || pagination.total_pages <= 1) {
      return null;
    }

    const { current_page, total_pages, has_previous, has_next } = pagination;
    const items = [];

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={!has_previous}
        onClick={() => has_previous && handlePageChange(current_page - 1, type)}
      />
    );

    const startPage = Math.max(1, current_page - 2);
    const endPage = Math.min(total_pages, current_page + 2);

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === current_page}
          onClick={() => handlePageChange(page, type)}
        >
          {page}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={!has_next}
        onClick={() => has_next && handlePageChange(current_page + 1, type)}
      />
    );

    return (
      <div className="d-flex justify-content-center mt-4">
        <Pagination>{items}</Pagination>
      </div>
    );
  };

  const totalResults = (searchResults?.events.pagination.total_count || 0) + 
                       (searchResults?.activities.pagination.total_count || 0);

  const searchTitle = searchQuery 
    ? `Search: ${searchQuery} | Quicket`
    : 'Search Events & Activities | Quicket';
  const searchDescription = searchQuery
    ? `Search results for "${searchQuery}" - Find events and activities on Quicket`
    : 'Search for events, activities, and experiences on Quicket';

  return (
    <div className="search-page">
      <SEO
        title={searchTitle}
        description={searchDescription}
        type="website"
      />
      <Container>
        {/* Search Header */}
        <Row className="mb-5">
          <Col lg={10} className="mx-auto">
            <div className="search-header">
              <h1 className="search-page-title mb-4">
                {searchQuery ? (
                  <>
                    Search Results for <span className="text-primary">"{searchQuery}"</span>
                  </>
                ) : (
                  'Search Events & Activities'
                )}
              </h1>
              <Form onSubmit={handleSearch} className="search-form">
                <div className="search-input-wrapper">
                  <div className="search-icon-wrapper">
                    <i className="fas fa-search"></i>
                  </div>
                  <Form.Control
                    type="text"
                    size="lg"
                    placeholder="Search Events, Activities, Experiences..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <Button type="submit" variant="primary" size="lg" className="search-button">
                    <i className="fas fa-search me-2"></i>
                    Search
                  </Button>
                </div>
              </Form>
              
              {searchQuery && searchResults && (
                <div className="search-info">
                  <div className="d-flex align-items-center gap-2">
                    <i className="fas fa-info-circle text-primary"></i>
                    <p className="mb-0">
                      Found <strong className="text-primary">{totalResults}</strong> result{totalResults !== 1 ? 's' : ''}
                      {searchResults.events.pagination.total_count > 0 && (
                        <> • <strong>{searchResults.events.pagination.total_count}</strong> event{searchResults.events.pagination.total_count !== 1 ? 's' : ''}</>
                      )}
                      {searchResults.activities.pagination.total_count > 0 && (
                        <> • <strong>{searchResults.activities.pagination.total_count}</strong> activit{searchResults.activities.pagination.total_count !== 1 ? 'ies' : 'y'}</>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {error && (
          <Row>
            <Col>
              <Alert variant="danger">{error}</Alert>
            </Col>
          </Row>
        )}

        {loading && !searchResults ? (
          <>
            <Row className="mb-4">
              <Col>
                <SkeletonLoader type="text" width="200px" height="28px" className="mb-2" />
                <SkeletonLoader type="text" width="300px" height="18px" />
              </Col>
            </Row>
            <Row className="g-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <Col lg={4} md={6} key={index}>
                  <EventCardSkeleton />
                </Col>
              ))}
            </Row>
          </>
        ) : loading && searchResults ? (
          <Row className="g-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <Col lg={4} md={6} key={`skeleton-${index}`}>
                <EventCardSkeleton />
              </Col>
            ))}
          </Row>
        ) : searchResults && (searchResults.events.results.length > 0 || searchResults.activities.results.length > 0) ? (
          <>
            {/* Tabs for filtering results */}
            <Row>
              <Col>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab((k as any) || 'all')}
                  className="mb-4"
                >
                  <Tab eventKey="all" title={`All (${totalResults})`}>
                    {/* Events Section */}
                    {searchResults.events.results.length > 0 && (
                      <div className="results-section mb-5">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h2 className="h4">
                            <i className="fas fa-calendar-alt me-2 text-primary"></i>
                            Events ({searchResults.events.pagination.total_count})
                          </h2>
                        </div>
                        <Row className="g-4">
                          {searchResults.events.results.map((event) => (
                            <Col lg={4} md={6} key={event.id}>
                              <EventCard event={event} />
                            </Col>
                          ))}
                        </Row>
                        {renderPagination(searchResults.events.pagination, 'events')}
                      </div>
                    )}

                    {/* Activities Section */}
                    {searchResults.activities.results.length > 0 && (
                      <div className="results-section">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h2 className="h4">
                            <i className="fas fa-map-marked-alt me-2 text-primary"></i>
                            Activities ({searchResults.activities.pagination.total_count})
                          </h2>
                        </div>
                        <Row className="g-4">
                          {searchResults.activities.results.map((activity) => (
                            <Col lg={4} md={6} key={activity.id}>
                              <ActivityCard activity={activity} />
                            </Col>
                          ))}
                        </Row>
                        {renderPagination(searchResults.activities.pagination, 'activities')}
                      </div>
                    )}
                  </Tab>

                  <Tab eventKey="events" title={`Events (${searchResults.events.pagination.total_count})`}>
                    {searchResults.events.results.length > 0 ? (
                      <>
                        <Row className="g-4">
                          {searchResults.events.results.map((event) => (
                            <Col lg={4} md={6} key={event.id}>
                              <EventCard event={event} />
                            </Col>
                          ))}
                        </Row>
                        {renderPagination(searchResults.events.pagination, 'events')}
                      </>
                    ) : (
                      <div className="empty-state text-center py-5">
                        <div className="empty-state-icon mb-4">
                          <i className="fas fa-calendar-times"></i>
                        </div>
                        <h3 className="empty-state-title">No Events Found</h3>
                        <p className="empty-state-description">
                          We couldn't find any events matching your search.
                          <br />
                          Try adjusting your search query or browse all events.
                        </p>
                      </div>
                    )}
                  </Tab>

                  <Tab eventKey="activities" title={`Activities (${searchResults.activities.pagination.total_count})`}>
                    {searchResults.activities.results.length > 0 ? (
                      <>
                        <Row className="g-4">
                          {searchResults.activities.results.map((activity) => (
                            <Col lg={4} md={6} key={activity.id}>
                              <ActivityCard activity={activity} />
                            </Col>
                          ))}
                        </Row>
                        {renderPagination(searchResults.activities.pagination, 'activities')}
                      </>
                    ) : (
                      <div className="empty-state text-center py-5">
                        <div className="empty-state-icon mb-4">
                          <i className="fas fa-map-marked-alt"></i>
                        </div>
                        <h3 className="empty-state-title">No Activities Found</h3>
                        <p className="empty-state-description">
                          We couldn't find any activities matching your search.
                          <br />
                          Try adjusting your search query or browse all activities.
                        </p>
                      </div>
                    )}
                  </Tab>
                </Tabs>
              </Col>
            </Row>
          </>
        ) : searchQuery && !loading ? (
          <Row>
            <Col>
              <div className="empty-state text-center py-5">
                <div className="empty-state-icon mb-4">
                  <i className="fas fa-search"></i>
                </div>
                <h3 className="empty-state-title">No Results Found</h3>
                <p className="empty-state-description">
                  We couldn't find any results for "<strong>{searchQuery}</strong>".
                  <br />
                  Try adjusting your search terms or browse our popular categories.
                </p>
                <div className="mt-4 d-flex gap-2 justify-content-center flex-wrap">
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      setSearchQuery('');
                      navigate('/search');
                    }}
                  >
                    <i className="fas fa-times me-2"></i>
                    Clear Search
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/events')}
                  >
                    <i className="fas fa-calendar-alt me-2"></i>
                    Browse All Events
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        ) : null}
      </Container>
    </div>
  );
};

export default SearchPage;

